import { useState } from 'react'
import { com } from '@eidu/form'
import { Box, Button, CircularProgress, Menu, Stack, Typography } from '@mui/material'
import DraftFieldValue, { draftFieldValueToFieldValue } from '../../domain/entity/DraftFieldValue'
import hasFieldError from '../../domain/entity/hasFieldError'
import { logException } from '../../util/Logging'
import EntityRepository from '../../io/EntityRepository'
import usePaginatedEntitiesOfType from '../../io/usePaginatedEntitiesOfType'
import { useSnackBar } from '../feedback/SnackBarProvider'
import EntityFormInput from '../input/EntityFormInput'
import EqualityHashMap from '../../util/EqualityHashMap'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import Field = com.eidu.sharedlib.entity.field.Field
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString

export type BulkEditMenuProps = {
  field: Field
  anchorEl: HTMLElement | null
  onClose: () => void
  selectedEntityIds: string[]
  entityRepository: EntityRepository
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
  onEditComplete: () => void
}

const BulkEditMenu = ({
  field,
  anchorEl,
  onClose,
  selectedEntityIds,
  entityRepository,
  entityTypes,
  onEditComplete,
}: BulkEditMenuProps) => {
  const { showSnackBar } = useSnackBar()

  const referenceableEntityTypeIds = Array.from(field.validReferenceTypes?.asJsReadonlySetView() ?? [])
  const firstReferenceTypeId = referenceableEntityTypeIds[0]
  const shouldLoadEntities = referenceableEntityTypeIds.length === 1 && firstReferenceTypeId !== undefined

  const { entities: referenceableEntities } = usePaginatedEntitiesOfType({
    typeId: firstReferenceTypeId ?? entityTypes.keys().next().value,
    allTypes: entityTypes,
  })

  const referenceableEntitiesByTypeId: ReadonlyMap<
    EntityTypeId,
    ReadonlyMap<EntityId, EntityWithLabelAndRelated>
  > = shouldLoadEntities && firstReferenceTypeId
    ? new EqualityHashMap([
        [firstReferenceTypeId, new EqualityHashMap(referenceableEntities.map((e) => [e.entity.id, e]))],
      ])
    : new Map()

  const defaultFieldValue = { fieldId: field.id, type: field.type, value: '' }
  const [fieldValue, setFieldValue] = useState<DraftFieldValue>(defaultFieldValue)

  const [isEditing, setIsEditing] = useState(false)

  const hasError = field && hasFieldError(field, fieldValue, referenceableEntitiesByTypeId)

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          overflow: 'visible',
        },
      }}
    >
      <Box sx={{ p: 2, minWidth: 300, display: 'flex', flexDirection: 'column' }}>
        <Stack spacing={2}>
          <Typography variant="h6">Field ID</Typography>
          <Typography color="gray">{field.id.toString()}</Typography>
          <Typography variant="h6">Edit selected items</Typography>
          <EntityFormInput
            key={`column-form-field-${field.id.toString()}`}
            field={field}
            fieldValue={fieldValue}
            entityTypes={entityTypes}
            updateField={setFieldValue}
          />
          {selectedEntityIds.length === 0 && (
            <Typography variant="body2" color="warning.main" sx={{ fontStyle: 'italic' }}>
              Please select one or more rows for batch editing
            </Typography>
          )}
          <Stack direction="row" justifyContent="flex-end" sx={{ height: '40px' }}>
            <Button
              variant="contained"
              disabled={!selectedEntityIds.length || hasError || isEditing}
              onClick={async () => {
                setIsEditing(true)
                try {
                  const fieldValuesById = new Map([[field.id, draftFieldValueToFieldValue(fieldValue)]])
                  await entityRepository.modifyEntities(
                    new Map(
                      selectedEntityIds.map((id) => [
                        entityIdFromString(id),
                        {
                          valuesToUpdateByFieldId: fieldValuesById,
                          linksToUpdate: new Map(),
                          externalValuesToUpdate: new Map(),
                        },
                      ])
                    )
                  )
                  showSnackBar(`Updated ${selectedEntityIds.length} items`, 'success')
                  onEditComplete()
                  onClose()
                } catch (e) {
                  logException(e)
                  showSnackBar(`Failed to update ${selectedEntityIds.length} items`, 'error')
                }
                setIsEditing(false)
              }}
            >
              {isEditing ? <CircularProgress size={24} /> : `Apply (${selectedEntityIds.length})`}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Menu>
  )
}

export default BulkEditMenu
